
import { useState, useEffect, useCallback, memo } from 'react';
import { Button } from '@/components/ui/button';
import LazyImage from './LazyImage';
import { useIsMobile } from '@/hooks/use-mobile';

// Neues Hero-Bild verwenden
const HERO_IMAGE = {
  src: "/lovable-uploads/325f03d8-bccf-406b-9c2c-55604d942890.png",
  lowQuality: "/lovable-uploads/325f03d8-bccf-406b-9c2c-55604d942890.png?width=10&quality=5",
  width: 1200,
  height: 800
};

// Create a memoized button to prevent unnecessary re-renders
const HeroButton = memo(({ onClick }: { onClick: () => void }) => (
  <Button
    variant="default"
    className="bg-blue hover:bg-blue-dark text-white rounded-full px-8 py-6 text-lg hover-lift"
    onClick={onClick}
  >
    Mehr erfahren
  </Button>
));

HeroButton.displayName = 'HeroButton';

// Extracted overlay component to improve render performance
const ImageOverlay = memo(() => (
  <div className="absolute inset-0">
    <div className="absolute inset-0 bg-gray-dark/20 bg-blend-overlay"></div>
    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-blue/20"></div>
    <div className="absolute top-0 left-0 w-full h-full bg-pink-500/10 clip-diagonal-tl"></div>
    <div className="absolute bottom-0 right-0 w-2/3 h-1/2 bg-blue-600/15 clip-diagonal-br"></div>
  </div>
));

ImageOverlay.displayName = 'ImageOverlay';

const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const isMobile = useIsMobile();
  
  // Preload the hero image on mount
  useEffect(() => {
    const img = new Image();
    img.src = `${HERO_IMAGE.src}?width=${HERO_IMAGE.width}&quality=70&format=webp`;
  }, []);
  
  // Memoize scroll handler to prevent recreation on each render
  const handleScrollToVorteile = useCallback(() => {
    const element = document.getElementById("vorteile");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <section className="relative flex items-center justify-center overflow-hidden pt-16 h-[70vh]" id="hero">
      <div className="absolute inset-0 z-0">
        <LazyImage
          src={HERO_IMAGE.src}
          lowQualityPreview={HERO_IMAGE.lowQuality}
          alt="SPTools Hero Bild"
          className="w-full h-full"
          fallbackSrc="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&w=400&q=40" 
          priority={true}
          showToasts={false}
          width={HERO_IMAGE.width}
          height={HERO_IMAGE.height}
          sizes={isMobile ? "100vw" : "100vw"}
          quality={70}
          objectFit="cover"
          onLoad={() => setIsLoaded(true)}
        />
        
        <ImageOverlay />
      </div>

      <div className={`container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 ${isMobile ? 'mt-[100px]' : '-mt-[120px]'}`}>
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between">
          <div className="lg:w-1/2 text-center lg:text-left mb-10 lg:mb-0">
            <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl md:text-5xl'} font-bold text-white mb-6 leading-tight whitespace-nowrap`}>
              SPTools für die Integration von TYPO3 CMS und Microsoft 365
            </h1>
            
            <h2 className={`${isMobile ? 'text-base' : 'text-lg md:text-xl'} text-white space-y-6 font-medium`}>
              Optimieren Sie Ihre Unternehmensprozesse durch die intelligente Verbindung von TYPO3 und Microsoft 365.
            </h2>
            
            <div className="flex flex-col sm:flex-row justify-center lg:justify-start mt-8">
              <HeroButton onClick={handleScrollToVorteile} />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <div className="h-24 bg-clip-polygon bg-gray-100"></div>
      </div>
    </section>
  );
};

export default HeroSection;
